// The contract overlay: seeding it, turning it back into a payload, and diffing it for the rewrite
// list. Pure, so the hook stays a hook and this stays testable.
//
// Seeded from the NORMALIZED duplicate — `buildDuplicate`'s output — never from the raw source. That
// is what keeps the legacy shapes out of the editor: a spec written with `score_jsonpath` has
// already been desugared into `scores[]` by the time these rows exist, so the operator never meets
// a key the API no longer accepts. It is also why `feed_name` cannot leak into a new score row.
//
// Seeding allocates row ids, so it runs ONCE per source (and once per toggle-on). Re-seeding inside
// a render would remount every input on every keystroke.

import { buildDuplicate } from './duplicate';
import { isModellable, toRows, toSchema, rootDescriptionOf, schemaInvariantGaps } from './schemaModel';
import { contractChanges } from './schemaEdits';

let uid = 0;
const nextId = (prefix) => {
  uid += 1;
  return `${prefix}-seed-${uid}`;
};

const isObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

// Any values work here: `output_extraction` does not depend on the id, version, prompt or
// description, and this is only ever read for its contract half.
const normalize = (definition) =>
  buildDuplicate(definition, { stem: 'seed', version: 1, prompt: '', description: '' }).spec;

const scoreRows = (scores) =>
  (Array.isArray(scores) ? scores : []).map((score) => ({
    id: nextId('score'),
    name: (isObject(score) && score.name) || '',
    jsonpath: (isObject(score) && score.jsonpath) || '',
    // Everything else the source declared on this score — per-score `extra_columns`, say — rides
    // along untouched. The form does not expose it, so it must not drop it either.
    raw: isObject(score) ? score : {},
  }));

const columnRows = (extraColumns) =>
  Object.keys(isObject(extraColumns) ? extraColumns : {}).map((name) => ({
    id: nextId('column'),
    name,
    jsonpath: extraColumns[name],
  }));

export const seedContract = (definition) => {
  const spec = normalize(definition);
  if (!spec) return null;

  const output = spec.output_extraction || {};
  const schema = output.output_schema === undefined || output.output_schema === null ? null : output.output_schema;
  const kind = schema ? 'schema' : 'prose';
  const modellable = kind === 'schema' && isModellable(schema);

  const model = {
    rows: modellable ? toRows(schema) : [],
    rootDescription: modellable ? rootDescriptionOf(schema) : '',
    scores: scoreRows(output.scores),
    extraColumns: columnRows(output.extra_columns),
  };

  const formatPrompt = (spec.score_gen || {}).format_prompt || '';

  return {
    kind,
    isEditing: false,
    // A shape the row editor cannot represent opens in the JSON pane rather than being flattened
    // into rows that would lose an `enum` or an array.
    useRaw: kind === 'schema' && !modellable,
    isModellable: modellable,
    rawSchema: schema,
    model,
    formatPrompt,
    blocked: null,
    adopted: false,
    seed: {
      model,
      formatPrompt,
      schemaJson: JSON.stringify(schema),
      // What `toSchema` will put right about a legacy shape on the way out. Reported, not performed
      // in silence — and only relevant while the row editor owns the schema.
      gaps: modellable ? schemaInvariantGaps(schema) : [],
    },
  };
};

// Rows derived from a hand-edited schema, adopting the ids of the rows they replace wherever the
// name still matches. Without this, a trip through the JSON pane would give every field a new id
// and the change list would report the whole shape as removed-and-re-added.
const adoptIds = (rows, previous) =>
  rows.map((row) => {
    const match = (previous || []).find((old) => old.name === row.name);
    if (!match) return row;
    return {
      ...row,
      id: match.id,
      children: Array.isArray(row.children) ? adoptIds(row.children, match.children) : row.children,
    };
  });

// Giving the COPY a declared shape. Not a migration of anything: the source keeps its prose, keeps
// loading and keeps running — this is the successor being authored, which is what the route is for.
// The schema arrives already derived from the paths this spec reads (schemaFromPaths.js), so it cannot
// fail the coherence check; installing it opens the editor, because adopting a shape and wanting to
// edit it are the same intent.
export const adoptSchema = (contract, schema) => {
  const modellable = isModellable(schema);
  return {
    kind: 'schema',
    adopted: true,
    isEditing: true,
    isModellable: modellable,
    useRaw: !modellable,
    rawSchema: schema,
    model: {
      ...contract.model,
      rows: modellable ? toRows(schema) : [],
      rootDescription: rootDescriptionOf(schema),
    },
    formatPrompt: '',
    blocked: null,
  };
};

// Leaving the JSON pane. The hand-edited schema becomes the rows, so the edit is not silently
// dropped by a return to the field editor.
export const exitRawSchema = (contract) => {
  const schema = contract.rawSchema;
  if (!isModellable(schema)) return { useRaw: true };
  return {
    useRaw: false,
    isModellable: true,
    model: {
      ...contract.model,
      rows: adoptIds(toRows(schema), contract.model.rows),
      rootDescription: rootDescriptionOf(schema),
    },
  };
};

// The schema this contract would send. The row editor owns it unless the JSON pane took over.
export const contractSchemaOf = (contract) => {
  if (!contract || contract.kind !== 'schema') return null;
  if (contract.useRaw) return contract.rawSchema;
  return toSchema(contract.model.rows, contract.model.rootDescription);
};

const columnsToMap = (rows) =>
  (rows || []).reduce((map, row) => {
    if (row.name) map[row.name] = row.jsonpath;
    return map;
  }, {});

// What `buildDuplicate` needs. Empty while the editor is closed, so a form nobody opened sends
// byte-for-byte what it sent before this feature existed.
export const contractOverrides = (contract) => {
  if (!contract || !contract.isEditing) return {};
  if (contract.kind === 'prose') return { formatPromptOverride: contract.formatPrompt };

  return {
    outputOverride: {
      output_schema: contractSchemaOf(contract),
      scores: (contract.model.scores || []).map((score) => ({
        ...score.raw,
        name: score.name,
        jsonpath: score.jsonpath,
      })),
      extra_columns: columnsToMap(contract.model.extraColumns),
    },
  };
};

export const contractIsDirty = (contract) => {
  if (!contract) return false;
  if (contract.adopted) return true;
  if (contract.kind === 'prose') return contract.formatPrompt !== contract.seed.formatPrompt;
  if (JSON.stringify(contractSchemaOf(contract)) !== contract.seed.schemaJson) return true;
  return contractChanges(contract.seed.model, contract.model).length > 0;
};

// The lines the rewrite list shows for contract edits. Field-level diffs come from row ids; the
// JSON pane has no rows to diff, so a hand-edited schema is reported as the one thing that is
// actually known about it.
export const contractChangeList = (contract) => {
  if (!contract || !contract.isEditing) return [];

  if (contract.kind === 'prose') {
    return contract.formatPrompt === contract.seed.formatPrompt ? [] : ['Rewrote the prose output contract.'];
  }

  // Adopting a shape is one change, not a field-by-field diff against a seed that had no fields. Rows
  // are neutralised so score and column edits still report — those ARE the operator's.
  if (contract.adopted) {
    return [
      'Declared an output_schema for this copy, derived from the paths it reads — the shape is enforced by the model’s grammar rather than requested in prose. The source keeps its prose and is untouched.',
    ].concat(contractChanges({ ...contract.seed.model, rows: contract.model.rows }, contract.model));
  }

  const changes = contractChanges(contract.seed.model, contract.model, contract.useRaw ? [] : contract.seed.gaps);
  if (contract.useRaw && JSON.stringify(contract.rawSchema) !== contract.seed.schemaJson) {
    return ['Replaced the answer shape by hand, in the JSON editor.'].concat(changes);
  }
  return changes;
};
